/**
 * Shared constants + state helpers for the "Get started" onboarding
 * affordances that live in two places:
 *   - The HomePage welcome checklist (4 steps)
 *   - The Sidebar "Get started" category (the same 4 items as nav)
 *
 * Both surfaces need the same CWS URL, the same skill download
 * descriptor, and the same notion of "has the user downloaded the
 * skill yet?" (the only completion flag not derived from the server).
 * Keeping them in one module avoids drift.
 */

/** Public Chrome Web Store listing for the Toado extension. */
export const TOADO_CWS_URL =
  'https://chromewebstore.google.com/detail/toado/lmmeeckpnachbbkilafepggbnnjhmkaj';

/** Public asset URL for the pre-built Claude Code skill. Served from
 *  apps/web/public/toado-triage.skill.md by the web app. */
export const SKILL_DOWNLOAD_HREF = '/toado-triage.skill.md';

/** Suggested filename for the downloaded skill — what the user sees
 *  in their Downloads folder. The asset path on disk is unrelated. */
export const SKILL_DOWNLOAD_FILENAME = 'SKILL.md';

/** localStorage key — per-device flag tracking whether the user has
 *  ever clicked the skill download button. Persists across reloads so
 *  the checkmark sticks; clearing browser data resets to "not yet",
 *  which is harmless (a re-download is idempotent). */
export const SKILL_DOWNLOADED_LS_KEY = 'vd-skill-downloaded';

/** Custom DOM event dispatched when the skill-downloaded flag flips
 *  via this module. The browser's native `storage` event only fires
 *  on OTHER tabs that share the same origin — same-tab listeners need
 *  this in-process event to stay in sync. HomePage + Sidebar both
 *  listen for it so the checkmark updates everywhere the moment the
 *  download is triggered, no matter which surface initiated it. */
export const SKILL_DOWNLOADED_EVENT = 'vd:skill-downloaded';

/** localStorage key — per-device "user-overridden" collapse state for
 *  the Get started sidebar section. When unset, the section defaults
 *  to expanded (unless all 4 steps are complete, in which case it
 *  defaults to collapsed). Once the user has clicked the chevron
 *  explicitly, their preference wins until they clear it. */
export const GET_STARTED_COLLAPSED_LS_KEY = 'vd-getstarted-collapsed';

export function readSkillDownloaded(): boolean {
  try {
    return localStorage.getItem(SKILL_DOWNLOADED_LS_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markSkillDownloaded(): void {
  try {
    localStorage.setItem(SKILL_DOWNLOADED_LS_KEY, 'true');
  } catch {
    // Safari private mode / quota — harmless; the in-memory state
    // still reflects the click for the current session.
  }
  // Fire the same-tab sync event regardless of localStorage success —
  // listeners care about "the user just downloaded the skill", not
  // about persistence. Persistence is a best-effort layer on top.
  try {
    window.dispatchEvent(new Event(SKILL_DOWNLOADED_EVENT));
  } catch {
    // SSR / no-window edge case
  }
}

/** Read explicit user-overridden collapse state. Returns null when
 *  unset (caller falls back to "all-done = collapsed, else expanded"). */
export function readGetStartedCollapsed(): boolean | null {
  try {
    const raw = localStorage.getItem(GET_STARTED_COLLAPSED_LS_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

export function writeGetStartedCollapsed(next: boolean): void {
  try {
    localStorage.setItem(GET_STARTED_COLLAPSED_LS_KEY, String(next));
  } catch {
    // ignore — same Safari-private-mode fallback as above
  }
}

/**
 * Trigger a download of the skill file. Creates a temporary anchor
 * with the `download` attribute, clicks it programmatically, removes
 * it. Better than `window.open(...)` because that opens a new tab to
 * the file URL; markdown files trigger inline display in some
 * browsers instead of saving.
 *
 * Also persists the downloaded flag so future renders of the home
 * checklist + sidebar item show the completed checkmark.
 */
export function downloadSkill(): void {
  if (typeof document === 'undefined') return;
  const a = document.createElement('a');
  a.href = SKILL_DOWNLOAD_HREF;
  a.download = SKILL_DOWNLOAD_FILENAME;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  markSkillDownloaded();
}
